// Reading a step, as opposed to editing it.
//
// Opening a step used to print its raw payload. That is the right thing
// for a developer and the wrong thing for everyone else: an author who
// clicks a step wants to know what it asks, what the options are and
// which one is right — not to parse braces for it.
//
// So this is a plain reading of the payload: every field, in order,
// under a readable name, with ids resolved to the words they stand for.
// It is deliberately generic rather than one view per kind. A bespoke
// view per kind would be twenty views to keep in step with the schema,
// and the first one to fall behind would quietly stop showing a field —
// precisely the failure a reading view must not have. Walking the
// payload cannot hide a field, because it never knew which fields to
// expect.
//
// Answer keys are shown, and marked. An author is checking that the
// right answer is right.
import { Check } from "lucide-react";
import {
  BASE_ZONES,
  PITCH_MARKINGS,
  ROLE_LABELS,
  markingLabel,
  roleLabel,
  zoneLabel,
} from "./geometry";

type Payload = Record<string, any>;

// Straight from the backend's ANSWER_KEY_FIELDS — the fields stripped
// before a payload is sent to a learner. Shown here, flagged.
const ANSWER_KEYS = new Set([
  "correctOptionId",
  "correctOptionIds",
  "correctZoneIds",
  "correctZoneId",
  "correctRoles",
  "correctMarkingIds",
  "correctPairs",
  "correctBucketByItem",
  "correctOrder",
  "correctPath",
  "correctZoneByRole",
  "correctPlayerIds",
]);

// Only where the field name alone would read badly. Anything missing
// here falls back to its own name, spaced out — which for `primeText`
// gives "Prime text": clumsy, but never wrong and never stale.
const LABELS: Record<string, string> = {
  primeText: "Framing line",
  noticeQuestion: "What to notice",
  revealedReasoning: "The reasoning, revealed",
  explanation: "Explanation shown after answering",
  prompt: "Prompt",
  question: "Question",
  options: "Options",
  steps: "Steps",
  items: "Items",
  buckets: "Buckets",
  terms: "Terms",
  targets: "Meanings",
  beats: "Beats",
  frames: "Frames",
  players: "Players",
  correctOptionId: "Correct option",
  correctOptionIds: "Correct options",
  correctZoneIds: "Correct zones",
  correctZoneId: "Correct zone",
  correctMarkingIds: "Correct markings",
  correctRoles: "Correct roles",
  correctOrder: "Correct order",
  correctPath: "Correct path",
  correctPairs: "Correct pairing",
  correctBucketByItem: "Correct bucket for each item",
  correctZoneByRole: "Correct zone for each role",
  correctPlayerIds: "Correct players",
};

const ZONE_IDS = new Set(BASE_ZONES.map((z) => z.id));
const MARKING_IDS = new Set(PITCH_MARKINGS.map((m) => m.id));

function humanise(key: string): string {
  if (LABELS[key]) return LABELS[key];
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Ids are what the payload stores; words are what the author wrote.
// Every id in a payload is defined somewhere else in that same payload —
// an option id in `options`, a player id in `players` — or in one of the
// shared vocabularies, so one pass over the payload plus the three
// vocabularies resolves nearly all of them.
function buildLookup(payload: Payload): Map<string, string> {
  const map = new Map<string, string>();
  const scan = (value: unknown) => {
    if (!Array.isArray(value)) return;
    for (const item of value) {
      if (!item || typeof item !== "object") continue;
      const row = item as Payload;
      if (typeof row.id !== "string") continue;
      const text = row.text ?? row.label ?? row.term ?? row.name ?? row.title;
      if (typeof text === "string" && text) map.set(row.id, text);
    }
  };
  Object.values(payload ?? {}).forEach(scan);
  return map;
}

function resolve(value: unknown, lookup: Map<string, string>): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value !== "string") return String(value);
  const found = lookup.get(value);
  if (found) return found;
  if (ZONE_IDS.has(value)) return zoneLabel(value);
  if (MARKING_IDS.has(value)) return markingLabel(value);
  if (ROLE_LABELS[value]) return roleLabel(value);
  return value;
}

export default function PayloadSummary({ payload }: { payload: Payload }) {
  const p = payload ?? {};
  const lookup = buildLookup(p);
  const entries = Object.entries(p).filter(([, v]) => v !== undefined);

  if (!entries.length)
    return <p className="text-sm text-muted-foreground">This step is empty.</p>;

  return (
    <dl className="space-y-3">
      {entries.map(([key, value]) => (
        <Field key={key} name={key} value={value} lookup={lookup} />
      ))}
    </dl>
  );
}

function Field({
  name,
  value,
  lookup,
}: {
  name: string;
  value: unknown;
  lookup: Map<string, string>;
}) {
  const isAnswer = ANSWER_KEYS.has(name);
  return (
    <div
      className={
        "min-w-0 rounded-lg border px-3 py-2 " +
        (isAnswer ? "border-emerald-500/40 bg-emerald-500/5" : "bg-muted/30")
      }
    >
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {isAnswer && <Check className="h-3 w-3 text-emerald-600" />}
        <span className={isAnswer ? "text-emerald-700" : undefined}>
          {humanise(name)}
        </span>
      </dt>
      <dd className="mt-1 min-w-0 text-sm">
        <Value value={value} lookup={lookup} />
      </dd>
    </div>
  );
}

function Value({
  value,
  lookup,
}: {
  value: unknown;
  lookup: Map<string, string>;
}) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">—</span>;

  if (typeof value !== "object")
    return (
      <span className="whitespace-pre-wrap break-words">
        {resolve(value, lookup)}
      </span>
    );

  if (Array.isArray(value)) {
    if (!value.length)
      return <span className="text-muted-foreground">None</span>;

    // A list of plain values is a list of names, and names read better
    // side by side than stacked one per line.
    if (value.every((v) => v === null || typeof v !== "object"))
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span
              key={i}
              className="rounded border bg-background px-1.5 py-0.5 text-xs"
            >
              {/* Numbered, because in an ordering or a passing path the
                  position IS the content. */}
              {value.length > 1 && (
                <span className="mr-1 text-muted-foreground">{i + 1}.</span>
              )}
              {resolve(v, lookup)}
            </span>
          ))}
        </div>
      );

    return (
      <ol className="space-y-1.5">
        {value.map((item, i) => (
          <li key={i} className="flex min-w-0 gap-2">
            <span className="mt-0.5 shrink-0 text-xs font-bold tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <RowObject row={item as Payload} lookup={lookup} />
            </div>
          </li>
        ))}
      </ol>
    );
  }

  // A plain object is a mapping — item to bucket, role to zone — and
  // both sides of it are ids worth resolving.
  const rows = Object.entries(value as Payload);
  if (!rows.length) return <span className="text-muted-foreground">None</span>;
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex min-w-0 flex-wrap items-baseline gap-1.5">
          <span className="text-xs font-medium">{resolve(k, lookup)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="min-w-0 text-xs">
            <Value value={v} lookup={lookup} />
          </span>
        </div>
      ))}
    </div>
  );
}

// One object inside a list — an option, a step, a scene frame. Its main
// text leads; everything else trails behind it, small, so the shape of
// the list stays readable while nothing is hidden.
function RowObject({
  row,
  lookup,
}: {
  row: Payload;
  lookup: Map<string, string>;
}) {
  if (!row || typeof row !== "object")
    return <span className="break-words">{String(row)}</span>;

  const MAIN = ["text", "label", "term", "name", "title", "caption"];
  const mainKey = MAIN.find((k) => typeof row[k] === "string" && row[k]);
  const rest = Object.entries(row).filter(
    ([k, v]) => k !== mainKey && k !== "id" && v !== undefined
  );

  return (
    <div className="min-w-0">
      {mainKey ? (
        <span className="whitespace-pre-wrap break-words text-sm">
          {row[mainKey]}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">
          {typeof row.id === "string" ? resolve(row.id, lookup) : "—"}
        </span>
      )}
      {rest.length > 0 && (
        <div className="mt-0.5 space-y-0.5">
          {rest.map(([k, v]) => (
            <div key={k} className="flex min-w-0 flex-wrap gap-1.5 text-xs">
              <span className="text-muted-foreground">{humanise(k)}:</span>
              <span className="min-w-0">
                <Value value={v} lookup={lookup} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
